export interface SparklineOpts {
  width: number;
  height: number;
}

export interface SparklinePathResult {
  line: string;
  area: string;
}

export function sparklinePath(values: number[], opts: SparklineOpts): SparklinePathResult {
  const { width: w, height: h } = opts;
  if (values.length === 0) return { line: "", area: "" };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = values.length > 1 ? w / (values.length - 1) : 0;

  const pts = values.map((v, i) => {
    const x = i * stepX;
    const y = h - 4 - ((v - min) / range) * (h - 8);
    return [x, y] as const;
  });

  const line = pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");
  const area = pts.length > 1 ? `${line} L ${w},${h} L 0,${h} Z` : "";
  return { line, area };
}

export interface BarChartOpts {
  width: number;
  height: number;
  padding: { t: number; r: number; b: number; l: number };
}

export interface BarChartLayout {
  bars: { x: number; y: number; width: number; height: number; value: number; index: number; isWeekend: boolean }[];
  yTicks: number[];
  innerWidth: number;
  innerHeight: number;
}

export function barChartLayout(series: number[], opts: BarChartOpts, weekendFlags?: boolean[]): BarChartLayout {
  const { width, height, padding } = opts;
  const innerWidth = width - padding.l - padding.r;
  const innerHeight = height - padding.t - padding.b;
  const max = Math.max(0, ...series);
  const bw = series.length > 0 ? innerWidth / series.length : 0;

  const bars = series.map((v, i) => {
    const barH = max > 0 ? (v / max) * innerHeight : 0;
    const x = padding.l + i * bw;
    const y = padding.t + innerHeight - barH;
    const isWeekend = weekendFlags ? Boolean(weekendFlags[i]) : i % 7 === 5 || i % 7 === 6;
    return { x, y, width: bw, height: barH, value: v, index: i, isWeekend };
  });

  const steps = 4;
  const yTicks = max > 0
    ? Array.from({ length: steps + 1 }, (_, i) => Math.round((max / steps) * i))
    : [0];

  return { bars, yTicks, innerWidth, innerHeight };
}

export interface DonutInput {
  count: number;
  color: string;
  label: string;
}

export interface DonutOpts {
  size: number;
  stroke: number;
}

export interface DonutSegmentLayout {
  dash: number;
  offset: number;
  color: string;
  label: string;
  count: number;
  fraction: number;
}

export interface DonutLayout {
  segments: DonutSegmentLayout[];
  total: number;
  radius: number;
  circumference: number;
}

export function donutSegments(inputs: DonutInput[], opts: DonutOpts): DonutLayout {
  const radius = (opts.size - opts.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = inputs.reduce((s, x) => s + x.count, 0);
  if (total === 0) return { segments: [], total: 0, radius, circumference };

  let offset = 0;
  const segments: DonutSegmentLayout[] = inputs.map((s) => {
    const fraction = s.count / total;
    const dash = circumference * fraction;
    const seg = { dash, offset, color: s.color, label: s.label, count: s.count, fraction };
    offset += dash;
    return seg;
  });

  return { segments, total, radius, circumference };
}

export function evenlySpacedIndices(length: number, count: number): number[] {
  if (length <= 0) return [];
  if (length <= count) return Array.from({ length }, (_, i) => i);
  const out: number[] = [];
  for (let k = 0; k < count; k++) out.push(Math.round((k * (length - 1)) / (count - 1)));
  return out;
}

// ============================================================
//  Task 14: Layout-Helfer fuer StackedBarChart (Kanal-Stapel je Zeit-Abschnitt, inkl.
//  gemischt positiver/negativer Werte fuer Gutschriften). Rein additiv — barChartLayout,
//  donutSegments, sparklinePath und evenlySpacedIndices oben bleiben unveraendert, damit
//  BarChart/Donut/Sparkline ihr bestehendes Verhalten behalten.
// ============================================================

export interface StackedBucketInput {
  label: string;
  values: Record<string, number>;
}

export interface StackedSegmentLayout {
  channel: string;
  value: number;
  y: number;
  height: number;
}

export interface StackedBarLayout {
  x: number;
  width: number;
  index: number;
  label: string;
  segments: StackedSegmentLayout[];
  positiveTotal: number;
  negativeTotal: number;
}

export interface StackedBarChartOpts {
  width: number;
  height: number;
  padding: { t: number; r: number; b: number; l: number };
}

export interface StackedBarChartLayout {
  bars: StackedBarLayout[];
  yTicks: number[];
  zeroY: number;
  innerWidth: number;
  innerHeight: number;
  maxPositive: number;
  minNegative: number;
  // px pro Werte-Einheit oberhalb/unterhalb der Nulllinie — exportiert, damit Konsumenten
  // (z. B. die Y-Achsen-Beschriftung der Komponente) dieselbe Skala verwenden statt sie
  // fehleranfaellig neu herzuleiten.
  posScale: number;
  negScale: number;
}

// Ticks fuer eine Achse, die sowohl positive als auch negative Werte abdecken muss. 0 ist
// immer enthalten (Nulllinie). Positive Seite bekommt 4 Schritte (Konvention wie
// barChartLayout oben), negative Seite bewusst nur 2 Schritte — Gutschriften sind in der
// Praxis deutlich kleiner als der Umsatz, mehr Ticks dort waeren nur Rauschen.
export function stackedYTicks(maxPositive: number, minNegative: number): number[] {
  const ticks = new Set<number>([0]);
  if (maxPositive > 0) {
    const steps = 4;
    for (let i = 1; i <= steps; i++) ticks.add(Math.round((maxPositive / steps) * i));
  }
  if (minNegative < 0) {
    const steps = 2;
    for (let i = 1; i <= steps; i++) ticks.add(Math.round((minNegative / steps) * i));
  }
  return Array.from(ticks).sort((a, b) => a - b);
}

// Stapel-Layout mit Nulllinie: die verfuegbare innerHeight wird proportional in eine
// Positiv- und eine Negativ-Zone aufgeteilt (Anteil = Anteil am Gesamt-Wertebereich), die
// Nulllinie liegt an der Grenze. Ohne negative Werte verhaelt sich das exakt wie
// barChartLayout (Nulllinie ganz unten, volle Hoehe fuer Positiv-Werte). channels wird vom
// Aufrufer als vollstaendige, ueber alle buckets gueltige Kanalliste uebergeben (Vertrag von
// DashSeriesPayload.channels) — fehlt ein Kanal in einem bucket, wird er dort als 0
// behandelt und erzeugt kein Segment.
export function stackedBarChartLayout(
  buckets: StackedBucketInput[],
  channels: string[],
  opts: StackedBarChartOpts,
): StackedBarChartLayout {
  const { width, height, padding } = opts;
  const innerWidth = width - padding.l - padding.r;
  const innerHeight = height - padding.t - padding.b;
  const bw = buckets.length > 0 ? innerWidth / buckets.length : 0;

  let maxPositive = 0;
  let minNegative = 0;
  const sums = buckets.map((b) => {
    let pos = 0;
    let neg = 0;
    for (const ch of channels) {
      const v = b.values[ch] ?? 0;
      if (v > 0) pos += v;
      else if (v < 0) neg += v;
    }
    if (pos > maxPositive) maxPositive = pos;
    if (neg < minNegative) minNegative = neg;
    return { pos, neg };
  });

  const totalRange = maxPositive + Math.abs(minNegative);
  const positiveFraction = totalRange > 0 ? maxPositive / totalRange : 1;
  const positiveZoneHeight = innerHeight * positiveFraction;
  const negativeZoneHeight = innerHeight - positiveZoneHeight;
  const zeroY = padding.t + positiveZoneHeight;

  const posScale = maxPositive > 0 ? positiveZoneHeight / maxPositive : 0;
  const negScale = minNegative < 0 ? negativeZoneHeight / Math.abs(minNegative) : 0;

  const bars: StackedBarLayout[] = buckets.map((b, index) => {
    const x = padding.l + index * bw;
    let posAcc = 0;
    let negAcc = 0;
    const segments: StackedSegmentLayout[] = [];
    for (const ch of channels) {
      const v = b.values[ch] ?? 0;
      if (v === 0) continue;
      if (v > 0) {
        const segHeight = v * posScale;
        const y = zeroY - posAcc - segHeight;
        segments.push({ channel: ch, value: v, y, height: segHeight });
        posAcc += segHeight;
      } else {
        const segHeight = Math.abs(v) * negScale;
        const y = zeroY + negAcc;
        segments.push({ channel: ch, value: v, y, height: segHeight });
        negAcc += segHeight;
      }
    }
    const sum = sums[index] ?? { pos: 0, neg: 0 };
    return {
      x,
      width: bw,
      index,
      label: b.label,
      segments,
      positiveTotal: sum.pos,
      negativeTotal: sum.neg,
    };
  });

  return {
    bars,
    yTicks: stackedYTicks(maxPositive, minNegative),
    zeroY,
    innerWidth,
    innerHeight,
    maxPositive,
    minNegative,
    posScale,
    negScale,
  };
}
